import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { api, formatApiErrorDetail } from "../../api";
import { useAuth } from "../../AuthContext";
import { isAdminRole } from "../../../lib/roles";
import { Card, EmptyState, PageHeader, formatDate } from "../../ui";
import { Spinner, StatusBadge, fmtBRL } from "./shared";

const TabBar = ({ tabs, active, onChange }) => (
  <div className="mt-6 flex items-center gap-2 border-b border-slate-200">
    {tabs.map((t) => (
      <button
        key={t.key}
        data-testid={`expense-tab-${t.key}`}
        onClick={() => onChange(t.key)}
        className={`px-4 py-2 text-xs uppercase tracking-[0.22em] transition-colors ${
          active === t.key
            ? "text-[#002776] border-b-2 border-[#002776]"
            : "text-slate-500 hover:text-slate-800"
        }`}
      >
        {t.label}
      </button>
    ))}
  </div>
);

const ExpenseRow = ({ r }) => (
  <tr data-testid={`expense-row-${r.id}`} className="border-t border-slate-100 hover:bg-slate-50">
    <td className="px-3 py-2 font-medium">{r.title}</td>
    <td className="px-3 py-2 text-slate-600">{r.author_name}</td>
    <td className="px-3 py-2 text-slate-600">{r.payer_name || "—"}</td>
    <td className="px-3 py-2 text-xs text-slate-600">
      {formatDate(r.start_date)} — {formatDate(r.end_date)}
    </td>
    <td className="px-3 py-2 text-right">{fmtBRL(r.total)}</td>
    <td className="px-3 py-2">
      <StatusBadge status={r.status} />
    </td>
    <td className="px-3 py-2 text-right">
      <Link
        to={`/portal/despesas/${r.id}`}
        data-testid={`expense-open-${r.id}`}
        className="text-xs uppercase tracking-wide text-[#002776] hover:underline"
      >
        Abrir
      </Link>
    </td>
  </tr>
);

const HEADERS = ["Título", "Autor", "Pago por", "Período", "Total", "Status", "Ações"];
const RIGHT = new Set(["Total", "Ações"]);

const ExpensesTable = ({ rows }) => (
  <table className="min-w-full text-sm" data-testid="expenses-list-table">
    <thead className="bg-slate-50 text-[10px] uppercase tracking-wide text-slate-600">
      <tr>
        {HEADERS.map((h) => (
          <th key={h} className={`${RIGHT.has(h) ? "text-right" : "text-left"} px-3 py-2`}>
            {h}
          </th>
        ))}
      </tr>
    </thead>
    <tbody>
      {rows.map((r) => (
        <ExpenseRow key={r.id} r={r} />
      ))}
    </tbody>
  </table>
);

export function ExpensesList() {
  const { user } = useAuth();
  const isDirector = user?.role === "diretor";
  const isAdmin = isAdminRole(user?.role);
  const availableTabs = useMemo(() => {
    const t = [{ key: "mine", label: "Meus relatórios" }];
    if (isDirector) t.push({ key: "to_review", label: "Para aprovar" });
    if (isAdmin) t.push({ key: "all", label: "Todos" });
    return t;
  }, [isDirector, isAdmin]);
  const [tab, setTab] = useState("mine");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/portal/despesas", { params: { tab } });
      setRows(res.data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-8" data-testid="expenses-list-page">
      <PageHeader
        title="Restituição de Despesas"
        subtitle="Solicite a restituição de despesas pagas e acompanhe as aprovações."
        actions={
          <Link
            to="/portal/despesas/novo"
            data-testid="expense-new-btn"
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#002776] text-white text-[11px] uppercase tracking-[0.25em] hover:bg-[#001a5a] transition-colors"
          >
            <Plus size={14} /> Novo relatório
          </Link>
        }
      />
      {availableTabs.length > 1 && (
        <TabBar tabs={availableTabs} active={tab} onChange={setTab} />
      )}
      <Card className="mt-6 p-0 overflow-hidden">
        {loading ? (
          <Spinner />
        ) : rows.length === 0 ? (
          <EmptyState
            title="Nenhum relatório encontrado."
            hint="Clique em Novo relatório para criar um."
          />
        ) : (
          <ExpensesTable rows={rows} />
        )}
      </Card>
    </div>
  );
}
